import { useCallback } from 'react';
import { useQueries } from 'react-query';
import { SchemaFeatureFlags } from '@frontend/schema';
import { useOrgData } from './use-org-data';

// Transforms a location's flags response into a Set of enabled flag names.
// Built once per fetch via react-query's `select`, not on every render.
// Set lookups are O(1) vs O(n) for arrays. This can matter when it's locationCount * 616 (the current number of flags) per render
const selectEnabledFlagNames = (data: Awaited<ReturnType<typeof SchemaFeatureFlags.ListLocationFlags>>) =>
  new Set(
    data.flags
      ?.filter(({ value }) => value)
      .map(({ flag }) => flag?.name)
      .filter(Boolean) as string[]
  );

export const useOrgFeatureFlags = () => {
  const { locationIdsWithoutParent } = useOrgData();

  const ffQueries = useQueries(
    locationIdsWithoutParent.map((locationId) => {
      return {
        queryKey: [locationId, 'features'],
        queryFn: () => {
          return SchemaFeatureFlags.ListLocationFlags(
            { locationId: locationId },
            { headers: { 'Location-Id': locationId } }
          );
        },
        select: selectEnabledFlagNames,
      };
    })
  );

  const getFeatureFlagAllEnabled = useCallback(
    (flagName: string) => {
      // race condition: there can be no locations yet (i.e. [])
      if (!ffQueries.length) return false;
      // all locations must have the flag enabled
      return ffQueries.every((q) => q.data?.has(flagName));
    },
    [ffQueries]
  );

  const getFeatureFlagAnyEnabled = useCallback(
    (flagName: string) => {
      // race condition: there can be no locations yet (i.e. [])
      if (!ffQueries.length) return false;
      // at least one location must have the flag enabled
      return ffQueries.some((q) => q.data?.has(flagName));
    },
    [ffQueries]
  );

  return { getFeatureFlagAllEnabled, getFeatureFlagAnyEnabled };
};
